/** Shared helpers — mandatory document types from COMPANY/DIRECTOR_DOCUMENT_TYPE lookups. */

import { getDocumentTypeRules } from './documentTypeFields';

export const DEFAULT_MANDATORY_TYPE_CODES = [
  'trade_license',
  'moa',
  'aoa',
  'commercial_registration',
];

export const DEFAULT_DIRECTOR_MANDATORY_TYPE_CODES = [
  'passport',
  'emirates_id',
];

export function normalizeTypeKey(value) {
  return String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
}

export function findLookupItem(items, codeOrLabel) {
  const key = normalizeTypeKey(codeOrLabel);
  if (!key) return null;
  return (items || []).find(
    (item) => normalizeTypeKey(item.code) === key || normalizeTypeKey(item.label) === key
  ) || null;
}

export function lookupLabel(items, code) {
  const match = findLookupItem(items, code);
  return match?.label || String(code || '').replace(/_/g, ' ');
}

/**
 * Resolve stored settings to lookup codes (supports legacy label-only mandatoryTypes).
 */
export function resolveMandatoryTypeCodes(documentsSettings, lookupItems) {
  const docs = documentsSettings || {};
  if (Array.isArray(docs.mandatoryTypeCodes) && docs.mandatoryTypeCodes.length) {
    return docs.mandatoryTypeCodes.map((c) => normalizeTypeKey(c)).filter(Boolean);
  }
  if (Array.isArray(docs.mandatoryTypes) && docs.mandatoryTypes.length) {
    return docs.mandatoryTypes.map((entry) => {
      const match = findLookupItem(lookupItems, entry);
      return match?.code ? normalizeTypeKey(match.code) : normalizeTypeKey(entry);
    }).filter(Boolean);
  }
  return [...DEFAULT_MANDATORY_TYPE_CODES];
}

export function resolveDirectorMandatoryTypeCodes(documentsSettings, lookupItems) {
  const docs = documentsSettings || {};
  if (Array.isArray(docs.directorMandatoryTypeCodes) && docs.directorMandatoryTypeCodes.length) {
    return docs.directorMandatoryTypeCodes.map((c) => normalizeTypeKey(c)).filter(Boolean);
  }
  if (Array.isArray(docs.directorMandatoryTypes) && docs.directorMandatoryTypes.length) {
    return docs.directorMandatoryTypes.map((entry) => {
      const match = findLookupItem(lookupItems, entry);
      return match?.code ? normalizeTypeKey(match.code) : normalizeTypeKey(entry);
    }).filter(Boolean);
  }
  return [...DEFAULT_DIRECTOR_MANDATORY_TYPE_CODES];
}

export function documentTypeKeys(doc) {
  const label = normalizeTypeKey(doc.typeLabel || doc.type);
  return [
    doc.typeCode,
    doc.category,
    doc.doc_category,
    doc.doc_type,
    label,
  ].map(normalizeTypeKey).filter(Boolean);
}

export function docMatchesMandatoryType(doc, typeCode, lookupItems) {
  const target = normalizeTypeKey(typeCode);
  if (!target) return false;
  const label = normalizeTypeKey(lookupLabel(lookupItems, typeCode));
  const keys = documentTypeKeys(doc);
  return keys.some((c) => c === target || c === label);
}

export function documentStableKey(doc) {
  if (!doc) return '';
  const id = doc.documentId ?? doc.document_id ?? doc.id;
  if (id != null && id !== '' && !String(id).startsWith('row-')) {
    return `id:${id}`;
  }
  const type = normalizeTypeKey(doc.typeCode || doc.category || doc.doc_type || doc.type);
  const title = normalizeTypeKey(doc.title || doc.fileName || doc.name || '');
  const url = String(doc.fileUrl || doc.file_url || '');
  return `fallback:${type}:${title}:${url}`;
}

/** Prefer exact type-code match; each physical document fills at most one slot. */
export function findUploadedMandatoryDoc(documents, typeCode, lookupItems, usedKeys = new Set()) {
  const target = normalizeTypeKey(typeCode);
  const label = normalizeTypeKey(lookupLabel(lookupItems, typeCode));
  const available = (documents || []).filter((doc) => !usedKeys.has(documentStableKey(doc)));

  const exact = available.find((doc) => {
    const code = normalizeTypeKey(doc.typeCode || doc.category || doc.doc_category || doc.doc_type);
    return code && code === target;
  });
  if (exact) return exact;

  const byTitle = available.find((doc) => {
    const title = normalizeTypeKey(doc.title || doc.fileName || doc.name || '');
    return title && (title === label || title === target);
  });
  if (byTitle) return byTitle;

  const byTypeLabel = available.find((doc) => {
    const docLabel = normalizeTypeKey(doc.typeLabel || doc.type || '');
    return docLabel && docLabel === label;
  });
  if (byTypeLabel) return byTypeLabel;

  return null;
}

/**
 * Mandatory slots show one doc per required type; remaining docs appear under Other.
 */
export function partitionDocumentsByMandatory(documents, mandatoryTypeCodes, lookupItems) {
  const usedKeys = new Set();
  const documentSlots = (mandatoryTypeCodes || []).map((typeCode) => {
    const uploaded = findUploadedMandatoryDoc(documents, typeCode, lookupItems, usedKeys);
    if (uploaded) usedKeys.add(documentStableKey(uploaded));
    return {
      typeCode,
      typeLabel: lookupLabel(lookupItems, typeCode),
      uploaded,
    };
  });
  const otherDocuments = (documents || []).filter(
    (doc) => !usedKeys.has(documentStableKey(doc))
  );
  return { documentSlots, otherDocuments };
}

export function dedupeDocuments(documents) {
  const seen = new Set();
  const out = [];
  for (const doc of documents || []) {
    const key = documentStableKey(doc);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(doc);
  }
  return out;
}

export function getMissingMandatoryTypeCodes(documents, mandatoryTypeCodes) {
  const codes = mandatoryTypeCodes || [];
  return codes.filter((typeCode) => {
    const key = normalizeTypeKey(typeCode);
    const uploaded = (documents || []).some(
      (doc) => normalizeTypeKey(doc.typeCode || doc.type) === key
        || docMatchesMandatoryType(doc, typeCode, [])
    );
    return !uploaded;
  });
}

export function validateStoredDirectorDocument(doc, lookupItems) {
  if (!doc?.file) return null;
  const lookupItem = findLookupItem(lookupItems, doc.typeCode || doc.type);
  const rules = getDocumentTypeRules(lookupItem);
  if (rules.requiresDocumentNumber && !String(doc.documentNumber || '').trim()) {
    return `${lookupLabel(lookupItems, doc.typeCode || doc.type)} requires a document number`;
  }
  if (rules.requiresIssueDate && !doc.issueDate) {
    return `${lookupLabel(lookupItems, doc.typeCode || doc.type)} requires an issue date`;
  }
  if (rules.requiresExpiry && !doc.expiryDate) {
    return `${lookupLabel(lookupItems, doc.typeCode || doc.type)} requires an expiry date`;
  }
  return null;
}
